import { sortBy } from 'lodash';

import { WorkflowStepListConst } from '@shared/constants/workflow-step-list.const';
import { UserType } from '@shared/enums/user-type';
import { IRadioButtonOption, IUserRadioButtonOption } from '@shared/interfaces/radio-button-option.interface';
import { UserData } from '@shared/interfaces/user-data';
import { RestUserModel, RestUserModelExtended } from '@shared/interfaces/user.interface';

export const UserListPositions = {
  me: 10,
  defaultAssignee: 20,
  pm: 25,
  resident: 30,
  selected: 40,
  common: 100,
} as const;

export function getUserList(
  users: RestUserModelExtended[],
  labelAsFullName = false,
  defaultUsersOnTop = false,
  doNotDisplayAssigneeName = false,
  displayNameAsLabel = false
): IUserRadioButtonOption<number>[] {
  const map: any = {};
  const result = users
    .filter(item => !!item.id)
    .map(item => {
      let defaultLabel = `${item.firstName} ${item.lastName}`;
      if (!item.firstName && !item.lastName) {
        defaultLabel = item.email;
      }

      let label = '';
      if (item.firstName && item.lastName && !item?.isFullName && !displayNameAsLabel) {
        let charAmount = 1;

        if (!labelAsFullName) {
          label = `${item.firstName} ${item.lastName.substring(0, charAmount).toUpperCase()}`;
          while (map[label]) {
            charAmount++;
            label = (
              item.lastName.length >= charAmount
                ? `${item.firstName} ${item.lastName.substring(0, charAmount)}`
                : `${item.firstName} ${item.lastName} ${item.lastName.length - charAmount + 1}`
            ).trim();
          }
        } else {
          label = `${item.firstName} ${item.lastName}`;
        }
      } else if (displayNameAsLabel && item.displayName) {
        label = item.displayName;
      }
      if (!label) {
        label = defaultLabel;
      } else if (map[label]) {
        label = defaultLabel;
      }

      let sort: number = UserListPositions.common;
      if (item.isDefaultAssignee) {
        const stepName = WorkflowStepListConst.find(s => s.value === item.defaultAssigneeForStep)?.label;
        label += ` (default ${stepName} assignee)`;
        if (doNotDisplayAssigneeName) label = `Dynamic assignee (${stepName} default)`;
        sort = UserListPositions.defaultAssignee;
      }

      if (item.isResidentAssignee) {
        label += ` (resident assignee)`;
        sort = UserListPositions.resident;
      }

      if (item.isAssignToResident) {
        label = `Resident (dynamic)`;
        sort = UserListPositions.resident;
      }

      if (item.isAssignToPM) {
        label = `PM (dynamic)`;
        sort = UserListPositions.pm;
      }

      if (item.isMe && !item.isDefaultAssignee) {
        label += ' (me)';
        sort = sort = UserListPositions.me;
      }

      if (item.selected) {
        sort = UserListPositions.selected;
      }

      map[label] = true;
      const user = {
        value: item.id,
        label,
        tooltip: defaultLabel,
        userGroupType: item.userGroupType,
        sort,
        hidden: !!item.hidden,
      };
      if (item.lastName) user['additionalSearchLabel'] = item.lastName;

      return user;
    })
    .sort((a, b) => (a.label?.toLocaleLowerCase() < b.label?.toLocaleLowerCase() ? 1 : -1));
  return defaultUsersOnTop ? result.sort((a, b) => (a.sort > b.sort ? 1 : -1)) : result;
}

export function getUserDisplayName(user: Partial<RestUserModel>) {
  if (!user) {
    return '';
  }
  let defaultLabel = `${user.firstName} ${user.lastName}`;
  if (!user.firstName && !user.lastName) {
    defaultLabel = user.email;
  }
  let label = null;
  if (user.firstName && user.lastName) {
    label = `${user.firstName} ${user.lastName.substring(0, 1).toUpperCase()}`;
  } else if (!user.firstName && !user.lastName && user.displayName) {
    label = user.displayName;
  }
  return label || defaultLabel || user.displayName;
}

export function getUserLastName(user: Pick<RestUserModel, 'firstName' | 'lastName' | 'email'>): string {
  return user?.lastName;
}

export function getUserOrVendorList(users: RestUserModel[]): IRadioButtonOption<number>[] {
  return users
    .filter(item => !!item.id)
    .map<IRadioButtonOption<number>>(item => {
      return item.userGroupType === UserType.Contractor
        ? {
            value: item.id,
            label: item.companyName,
          }
        : {
            value: item.id,
            label: `${item.firstName} ${item.lastName}`,
          };
    });
}

export function getUserOrVendorItems(users: RestUserModel[]): IRadioButtonOption<RestUserModel>[] {
  return users
    .filter(item => !!item.id)
    .map<IRadioButtonOption<RestUserModel>>(item => {
      return item.userGroupType === UserType.Contractor
        ? {
            value: item,
            label: item.companyName,
          }
        : {
            value: item,
            label: `${item.firstName} ${item.lastName}`,
          };
    });
}

export function getUserItems(users: RestUserModel[]): IRadioButtonOption<RestUserModel>[] {
  return users
    .filter(item => !!item.id)
    .map<IRadioButtonOption<RestUserModel>>(item => {
      return {
        value: item,
        label: `${item.firstName} ${item.lastName}`,
      };
    });
}

export function getSortedUserItems(users: RestUserModel[], userData?: UserData): IRadioButtonOption<number>[] {
  return sortBy(
    users
      .filter(item => !!item.id)
      .map<IRadioButtonOption<number>>(item => {
        return {
          value: item.id,
          label: getUserDisplayName(item),
          additionalSearchLabel: getUserLastName(item),
        };
      }),
    [item => item.label.toUpperCase()]
  );
}

export function getInternalUserList(users: RestUserModel[]): IRadioButtonOption<number>[] {
  return users
    .filter(item => !!item.id && item.userGroupType === UserType.Employee)
    .map<IRadioButtonOption<number>>(item => {
      return {
        value: item.id,
        label: `${item.firstName} ${item.lastName}`,
      };
    });
}

export function currentLabelMe(label: string): string {
  return `${label} (me)`;
}
